import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  BarChart3,
  BellRing,
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  CreditCard,
  Eye,
  Info,
  Loader2,
  RefreshCw,
  RotateCcw,
  Search,
  X,
} from 'lucide-react';
import DateRangeFilter from '../../components/DateRangeFilter';
import { useAppOptions } from '../../hooks/useAppOptions';
import { useDebounce } from '../../hooks/useDebounce';
import { payReportApi } from '../../utils/api';
import {
  CURRENCY_OPTIONS,
  daysAgo,
  formatMoney,
  formatTimestamp,
  notifyStatusMeta,
  payStatusMeta,
  today,
} from '../../utils/format';
import type {
  PayHistoryDetail,
  PayHistoryItem,
  PayHistoryListResponse,
} from '../../types/api';

const PAGE_SIZES = [20, 50, 100];

interface FilterState {
  appId: string;
  start: string;
  end: string;
  status: string;
  currency: string;
  channel: string;
  orderSn: string;
  outTradeNo: string;
  transactionId: string;
}

const initialFilters: FilterState = {
  appId: '',
  start: daysAgo(6),
  end: today(),
  status: 'all',
  currency: '',
  channel: '',
  orderSn: '',
  outTradeNo: '',
  transactionId: '',
};

const StatusBadge: React.FC<{ status: number }> = ({ status }) => {
  const meta = payStatusMeta(status);
  return (
    <span className={`inline-flex px-2 py-1 rounded text-xs font-medium border ${meta.className}`}>
      {meta.label}
    </span>
  );
};

const NotifyBadge: React.FC<{ status: number }> = ({ status }) => {
  const meta = notifyStatusMeta(status);
  return (
    <span className={`inline-flex px-2 py-1 rounded text-xs font-medium border ${meta.className}`}>
      {meta.label}
    </span>
  );
};

function dateTimeParts(text?: string, timestamp?: number) {
  let value = text || '';
  if (!value && timestamp) {
    value = new Date(timestamp * 1000).toLocaleString('sv-SE');
  }
  const spaceIndex = value.indexOf(' ');
  if (spaceIndex === -1) return { date: value || '-', time: '' };
  return { date: value.slice(0, spaceIndex), time: value.slice(spaceIndex + 1) };
}

const TimeCell: React.FC<{ text?: string; timestamp: number }> = ({ text, timestamp }) => {
  const parts = dateTimeParts(text, timestamp);
  return (
    <>
      <div className="text-zinc-300 text-xs whitespace-nowrap">{parts.date}</div>
      {parts.time && (
        <div className="text-zinc-500 text-[11px] mt-0.5 whitespace-nowrap">{parts.time}</div>
      )}
    </>
  );
};

const DetailField: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <div className="min-w-0">
    <div className="text-xs text-zinc-500 mb-1">{label}</div>
    <div className={`text-sm text-zinc-200 break-words ${mono ? 'font-mono' : ''}`}>{value || '-'}</div>
  </div>
);

const JsonBlock: React.FC<{ title: string; data: Record<string, unknown> | null }> = ({
  title,
  data,
}) => (
  <details className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden group">
    <summary className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer select-none text-sm text-zinc-300 hover:text-white transition-colors">
      {title}
      <span className="text-xs text-zinc-500 group-open:hidden">View</span>
      <span className="text-xs text-zinc-500 hidden group-open:inline">Hide</span>
    </summary>
    <div className="border-t border-zinc-800">
      {data ? (
        <pre className="px-4 py-3 text-xs text-zinc-300 overflow-auto max-h-72 leading-relaxed">
          {JSON.stringify(data, null, 2)}
        </pre>
      ) : (
        <div className="px-4 py-3 text-xs text-zinc-500">Not available for this order</div>
      )}
    </div>
  </details>
);

const CopyButton: React.FC<{ value: string }> = ({ value }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="p-1 rounded text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors"
      title="Copy"
    >
      {copied ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
    </button>
  );
};

const PaymentRecordsPage: React.FC = () => {
  const { apps } = useAppOptions();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [result, setResult] = useState<PayHistoryListResponse | null>(null);
  const [records, setRecords] = useState<PayHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const requestIdRef = useRef(0);

  const debouncedOrderSn = useDebounce(filters.orderSn);
  const debouncedOutTradeNo = useDebounce(filters.outTradeNo);
  const debouncedTransactionId = useDebounce(filters.transactionId);

  const [detailId, setDetailId] = useState<number | null>(() => {
    const orderParam = Number(searchParams.get('order'));
    return Number.isFinite(orderParam) && orderParam > 0 ? orderParam : null;
  });
  const [detail, setDetail] = useState<PayHistoryDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const openDetail = (id: number) => {
    setDetailId(id);
    const next = new URLSearchParams(searchParams);
    next.set('order', String(id));
    setSearchParams(next, { replace: true });
  };

  const closeDetail = () => {
    setDetailId(null);
    const next = new URLSearchParams(searchParams);
    next.delete('order');
    setSearchParams(next, { replace: true });
  };

  const updateFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters((previous) => ({ ...previous, [key]: value }));
  };

  // 过滤条件变化时回到第一页
  useEffect(() => {
    setPage(1);
  }, [
    filters.appId,
    filters.start,
    filters.end,
    filters.status,
    filters.currency,
    filters.channel,
    debouncedOrderSn,
    debouncedOutTradeNo,
    debouncedTransactionId,
    pageSize,
  ]);

  const fetchRecords = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError('');
    try {
      const response = await payReportApi.getHistory({
        app_id: filters.appId || undefined,
        start_time: filters.start,
        end_time: filters.end,
        currency: filters.currency || undefined,
        status: filters.status,
        channel: filters.channel || undefined,
        order_sn: debouncedOrderSn.trim() || undefined,
        out_trade_no: debouncedOutTradeNo.trim() || undefined,
        transaction_id: debouncedTransactionId.trim() || undefined,
        page,
        page_size: pageSize,
      });

      if (requestId !== requestIdRef.current) return;

      if (response.code === 1 && response.data) {
        setResult(response.data);
        setRecords(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        setResult(null);
        setRecords([]);
        setError(response.msg || 'Failed to load payment records');
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error('Failed to fetch payment records:', err);
      setResult(null);
      setRecords([]);
      setError('Network error, please try again');
    }
    if (requestId === requestIdRef.current) setLoading(false);
  }, [
    filters.appId,
    filters.start,
    filters.end,
    filters.currency,
    filters.status,
    filters.channel,
    debouncedOrderSn,
    debouncedOutTradeNo,
    debouncedTransactionId,
    page,
    pageSize,
  ]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords, reloadKey]);

  useEffect(() => {
    if (detailId === null) return;
    let cancelled = false;

    (async () => {
      setDetailLoading(true);
      setDetailError('');
      setDetail(null);
      try {
        const response = await payReportApi.getHistoryDetail(detailId);
        if (!cancelled) {
          if (response.code === 1 && response.data) {
            setDetail(response.data);
          } else {
            setDetailError(response.msg || 'Failed to load order detail');
          }
        }
      } catch (err) {
        console.error('Failed to fetch payment detail:', err);
        if (!cancelled) setDetailError('Network error, please try again');
      }
      if (!cancelled) setDetailLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [detailId]);

  // 详情抽屉打开时锁定页面滚动 + Esc 关闭
  useEffect(() => {
    if (detailId === null) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeDetail();
    };
    window.addEventListener('keydown', handleKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [detailId]);

  const resetFilters = () => {
    setFilters({ ...initialFilters, start: daysAgo(6), end: today() });
    setPageSize(20);
    setPage(1);
  };

  const total = result?.total ?? 0;
  const perPage = result?.per_page ?? pageSize;
  const lastPage = result?.last_page ?? 1;

  return (
    <div className="space-y-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Payment Records</h1>
          <p className="text-zinc-400 mt-1">
            Every payment order created by your applications, with delivery logs
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/applications/payment-report"
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm rounded-lg transition-colors"
          >
            <BarChart3 size={16} />
            Payment report
          </Link>
          <button
            onClick={() => setReloadKey((key) => key + 1)}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
        <div className="flex flex-col xl:flex-row xl:flex-wrap xl:items-center gap-2">
          <select
            value={filters.appId}
            onChange={(e) => updateFilter('appId', e.target.value)}
            className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-zinc-600 transition-colors xl:min-w-[180px] shrink-0"
          >
            <option value="">All applications</option>
            {apps.map((app) => (
              <option key={app.id} value={String(app.id)}>
                {app.name}
              </option>
            ))}
          </select>

          <select
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value)}
            className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-zinc-600 transition-colors"
          >
            <option value="all">All status</option>
            <option value="0">Pending</option>
            <option value="1">Paid</option>
            <option value="2">Refunded</option>
            <option value="3">Closed</option>
          </select>

          <select
            value={filters.currency}
            onChange={(e) => updateFilter('currency', e.target.value)}
            className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-zinc-600 transition-colors"
          >
            <option value="">All currencies</option>
            {CURRENCY_OPTIONS.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>

          <select
            value={filters.channel}
            onChange={(e) => updateFilter('channel', e.target.value)}
            className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-zinc-600 transition-colors"
          >
            <option value="">All channels</option>
            <option value="stripe">Stripe</option>
          </select>

          <DateRangeFilter
            start={filters.start}
            end={filters.end}
            onChange={(start, end) => {
              updateFilter('start', start);
              updateFilter('end', end);
            }}
          />

          <button
            onClick={resetFilters}
            className="flex items-center gap-1.5 px-3 py-2 text-zinc-400 hover:text-white text-sm transition-colors xl:ml-auto"
          >
            <RotateCcw size={14} />
            Reset
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
              size={15}
            />
            <input
              type="text"
              value={filters.orderSn}
              onChange={(e) => updateFilter('orderSn', e.target.value)}
              placeholder="System order number"
              className="w-full pl-9 pr-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500 focus:outline-none focus:border-zinc-600 transition-colors"
            />
          </div>
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
              size={15}
            />
            <input
              type="text"
              value={filters.outTradeNo}
              onChange={(e) => updateFilter('outTradeNo', e.target.value)}
              placeholder="Merchant order number"
              className="w-full pl-9 pr-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500 focus:outline-none focus:border-zinc-600 transition-colors"
            />
          </div>
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
              size={15}
            />
            <input
              type="text"
              value={filters.transactionId}
              onChange={(e) => updateFilter('transactionId', e.target.value)}
              placeholder="Transaction ID (exact)"
              className="w-full pl-9 pr-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm placeholder-zinc-500 focus:outline-none focus:border-zinc-600 transition-colors"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-400/10 border border-red-400/20 rounded-xl p-4 text-red-400 text-sm flex items-center justify-between gap-4">
          <span>{error}</span>
          <button
            onClick={() => setReloadKey((key) => key + 1)}
            className="px-3 py-1.5 bg-red-400/10 hover:bg-red-400/20 rounded-lg transition-colors shrink-0"
          >
            Retry
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1080px]">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left text-zinc-400 text-xs font-medium px-4 py-3.5 whitespace-nowrap">Order</th>
                <th className="text-left text-zinc-400 text-xs font-medium px-4 py-3.5 whitespace-nowrap">
                  Merchant order
                </th>
                <th className="text-left text-zinc-400 text-xs font-medium px-4 py-3.5">Subject</th>
                <th className="text-right text-zinc-400 text-xs font-medium px-4 py-3.5 whitespace-nowrap">Amount</th>
                <th className="text-left text-zinc-400 text-xs font-medium px-4 py-3.5 whitespace-nowrap">Status</th>
                <th className="text-left text-zinc-400 text-xs font-medium px-4 py-3.5 whitespace-nowrap">Notify</th>
                <th className="text-left text-zinc-400 text-xs font-medium px-4 py-3.5 whitespace-nowrap">Time</th>
                <th className="text-right text-zinc-400 text-xs font-medium px-4 py-3.5" />
              </tr>
            </thead>
            <tbody>
              {loading && records.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-16 text-center text-zinc-400">
                    <Loader2 size={20} className="animate-spin inline-block mr-2" />
                    Loading records...
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-16 text-center text-zinc-500 text-sm">
                    No payment records found for the selected filters
                  </td>
                </tr>
              ) : (
                records.map((record) => (
                  <tr
                    key={record.id}
                    onClick={() => openDetail(record.id)}
                    className="border-b border-zinc-800 last:border-0 hover:bg-zinc-800/40 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-4">
                      <div className="text-white text-sm font-mono truncate max-w-[175px]" title={record.order_sn}>
                        {record.order_sn}
                      </div>
                      <div className="text-zinc-500 text-xs mt-0.5">#{record.id}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-zinc-300 text-sm font-mono truncate max-w-[150px]" title={record.out_trade_no}>
                        {record.out_trade_no}
                      </div>
                      <div className="text-zinc-500 text-xs mt-0.5 truncate max-w-[150px]">
                        {record.app_name || `app #${record.app_id}`}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-zinc-300 text-sm truncate max-w-[160px]" title={record.subject}>
                        {record.subject || <span className="text-zinc-600">-</span>}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right whitespace-nowrap">
                      <div className="text-white text-sm font-medium">
                        {formatMoney(record.amount, record.currency)}
                      </div>
                      <div className="text-zinc-500 text-xs mt-0.5">
                        {record.pay_fee && record.pay_fee !== record.amount
                          ? `paid ${formatMoney(record.pay_fee, record.currency)}`
                          : record.channel_text || record.channel}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <StatusBadge status={record.status} />
                    </td>
                    <td className="px-4 py-4">
                      <NotifyBadge status={record.notify_status} />
                    </td>
                    <td className="px-4 py-4">
                      <TimeCell text={record.paytime_text} timestamp={record.paytime} />
                    </td>
                    <td className="px-4 py-4 text-right">
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          openDetail(record.id);
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-md transition-colors"
                      >
                        <Eye size={14} />
                        Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-4 text-zinc-400 text-sm">
          <span>
            {total > 0
              ? `Showing ${(page - 1) * perPage + 1}-${Math.min(page * perPage, total)} of ${total}`
              : 'No records'}
          </span>
          <label className="flex items-center gap-2 text-xs text-zinc-500">
            Rows
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="px-2 py-1 bg-zinc-800 border border-zinc-700 rounded-md text-zinc-300 text-xs focus:outline-none focus:border-zinc-600"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        </div>
        {lastPage > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((previous) => Math.max(1, previous - 1))}
              disabled={page <= 1 || loading}
              className="flex items-center gap-1 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm transition-colors"
            >
              <ChevronLeft size={16} />
              Prev
            </button>
            <span className="text-zinc-400 text-sm px-2">
              Page {page} / {lastPage}
            </span>
            <button
              onClick={() => setPage((previous) => Math.min(lastPage, previous + 1))}
              disabled={page >= lastPage || loading}
              className="flex items-center gap-1 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm transition-colors"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {detailId !== null && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => closeDetail()}
          />
          <aside className="absolute right-0 top-0 h-full w-full max-w-2xl bg-zinc-950 border-l border-zinc-800 overflow-y-auto">
            <div className="sticky top-0 z-10 flex items-center justify-between gap-4 px-6 py-4 bg-zinc-950/95 backdrop-blur border-b border-zinc-800">
              <div>
                <div className="text-xs text-zinc-500">Payment order</div>
                <div className="text-white text-sm font-mono mt-0.5">
                  {detail?.order.order_sn || `#${detailId}`}
                </div>
              </div>
              <button
                onClick={() => closeDetail()}
                className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {detailLoading ? (
                <div className="space-y-4">
                  <div className="h-24 rounded-xl bg-zinc-900 animate-pulse" />
                  <div className="h-40 rounded-xl bg-zinc-900 animate-pulse" />
                  <div className="h-40 rounded-xl bg-zinc-900 animate-pulse" />
                </div>
              ) : detailError ? (
                <div className="bg-red-400/10 border border-red-400/20 rounded-xl p-4 text-red-400 text-sm">
                  {detailError}
                </div>
              ) : detail ? (
                <>
                  {/* Summary */}
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <div className="text-zinc-400 text-xs">Amount</div>
                        <div className="text-3xl font-bold text-white mt-1">
                          {formatMoney(detail.order.amount, detail.order.currency)}
                        </div>
                        {detail.order.pay_fee && detail.order.pay_fee !== detail.order.amount && (
                          <div className="text-zinc-500 text-xs mt-1">
                            Paid{' '}
                            {formatMoney(detail.order.pay_fee, detail.order.currency)}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex items-center gap-2">
                          <StatusBadge status={detail.order.status} />
                          <NotifyBadge
                            status={
                              detail.notify_logs.length === 0
                                ? 0
                                : detail.notify_logs.some((log) => log.is_success === 1)
                                  ? 1
                                  : 2
                            }
                          />
                        </div>
                        <span className="text-[11px] text-zinc-500">
                          {detail.snapshot ? 'Request snapshot' : 'Legacy order (reconstructed)'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Order info */}
                  <section className="space-y-4">
                    <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                      <Info size={15} className="text-zinc-500" />
                      Order information
                    </h3>
                    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                      <DetailField
                        label="System order number"
                        value={
                          <span className="inline-flex items-center gap-1">
                            <span className="font-mono break-all">{detail.order.order_sn}</span>
                            <CopyButton value={detail.order.order_sn} />
                          </span>
                        }
                      />
                      <DetailField
                        label="Merchant order number"
                        value={<span className="font-mono break-all">{detail.order.out_trade_no}</span>}
                      />
                      <DetailField label="Subject" value={detail.order.subject} />
                      <DetailField
                        label="Channel"
                        value={
                          <span className="inline-flex items-center gap-1.5">
                            <CreditCard size={13} className="text-zinc-500" />
                            {detail.order.channel_text || detail.order.channel}
                          </span>
                        }
                      />
                      <DetailField
                        label="Transaction ID"
                        value={
                          detail.order.transaction_id ? (
                            <span className="inline-flex items-center gap-1">
                              <span className="font-mono break-all text-xs">{detail.order.transaction_id}</span>
                              <CopyButton value={detail.order.transaction_id} />
                            </span>
                          ) : (
                            <span className="text-zinc-600">-</span>
                          )
                        }
                      />
                      <DetailField
                        label="Application"
                        value={
                          detail.app ? (
                            <span>
                              {detail.app.name}
                              <span className="block text-xs text-zinc-500 font-mono mt-0.5">
                                {detail.app.appid}
                              </span>
                            </span>
                          ) : (
                            <span className="text-zinc-600">Deleted application</span>
                          )
                        }
                      />
                      <DetailField
                        label="Created at"
                        value={detail.order.createtime_text || formatTimestamp(detail.order.createtime)}
                      />
                      <DetailField
                        label="Paid at"
                        value={detail.order.paytime_text || formatTimestamp(detail.order.paytime)}
                      />
                    </div>
                  </section>

                  {/* Request / response snapshots */}
                  <section className="space-y-3">
                    <h3 className="text-sm font-semibold text-white">API snapshots</h3>
                    <JsonBlock title="Create request" data={detail.request} />
                    <JsonBlock title="Create response" data={detail.response} />
                  </section>

                  {/* Status logs */}
                  <section className="space-y-3">
                    <h3 className="text-sm font-semibold text-white">Status history</h3>
                    {detail.logs.length === 0 ? (
                      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-sm text-zinc-500">
                        No status changes recorded
                      </div>
                    ) : (
                      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
                        {detail.logs.map((log, index) => (
                          <div key={`${log.createtime}-${index}`} className="flex gap-3">
                            <div className="flex flex-col items-center pt-1">
                              <span className="w-2 h-2 rounded-full bg-emerald-400" />
                              {index < detail.logs.length - 1 && (
                                <span className="w-px flex-1 bg-zinc-800 mt-1" />
                              )}
                            </div>
                            <div className="pb-1">
                              <div className="text-sm text-zinc-200">
                                {payStatusMeta(log.from_status).label}
                                <span className="text-zinc-600 mx-1.5">→</span>
                                {payStatusMeta(log.to_status).label}
                              </div>
                              <div className="text-xs text-zinc-500 mt-0.5">
                                {log.remark || 'Status updated'}
                              </div>
                              <div className="text-[11px] text-zinc-600 mt-0.5">
                                {log.createtime_text || formatTimestamp(log.createtime)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>

                  {/* Notify logs */}
                  <section className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                        <BellRing size={15} className="text-zinc-500" />
                        Notify attempts
                      </h3>
                      <span className="text-xs text-zinc-500">{detail.notify_logs.length} total</span>
                    </div>
                    {detail.notify_logs.length === 0 ? (
                      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-sm text-zinc-500">
                        No notify attempts yet
                      </div>
                    ) : (
                      detail.notify_logs.map((log) => (
                        <div key={log.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-3">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span
                              className={`inline-flex px-2 py-1 rounded text-xs font-medium border ${
                                log.is_success === 1
                                  ? 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20'
                                  : 'text-red-400 bg-red-400/10 border-red-400/20'
                              }`}
                            >
                              {log.is_success === 1 ? 'Delivered' : 'Failed'}
                            </span>
                            <span className="text-xs text-zinc-500">
                              HTTP {log.http_code || '-'} · {log.duration_ms} ms
                            </span>
                          </div>
                          <div className="text-xs text-zinc-400 font-mono break-all">{log.notify_url}</div>
                          {log.error_msg && (
                            <div className="text-xs text-red-400">{log.error_msg}</div>
                          )}
                          <div className="text-[11px] text-zinc-600">
                            {log.createtime_text || formatTimestamp(log.createtime)}
                          </div>
                          <details className="bg-zinc-950 border border-zinc-800 rounded-lg overflow-hidden group">
                            <summary className="px-3 py-2 text-xs text-zinc-400 cursor-pointer select-none hover:text-white transition-colors">
                              Request / response payload
                            </summary>
                            <div className="border-t border-zinc-800 divide-y divide-zinc-800">
                              <div className="p-3">
                                <div className="text-[11px] text-zinc-500 mb-1">Headers</div>
                                <pre className="text-[11px] text-zinc-300 overflow-auto max-h-40 leading-relaxed">
                                  {log.request_header || '-'}
                                </pre>
                              </div>
                              <div className="p-3">
                                <div className="text-[11px] text-zinc-500 mb-1">Request body</div>
                                <pre className="text-[11px] text-zinc-300 overflow-auto max-h-40 leading-relaxed">
                                  {log.request_body || '-'}
                                </pre>
                              </div>
                              <div className="p-3">
                                <div className="text-[11px] text-zinc-500 mb-1">Response</div>
                                <pre className="text-[11px] text-zinc-300 overflow-auto max-h-40 leading-relaxed">
                                  {log.response_body || '-'}
                                </pre>
                              </div>
                            </div>
                          </details>
                        </div>
                      ))
                    )}
                  </section>
                </>
              ) : null}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default PaymentRecordsPage;
